/**
 * ProductWorkspace — product-first authoring workspace.
 *
 * BUILD TOM I / 1:
 * - VOLUME products use a premium Overview / Content / Settings workspace.
 * - Editorial is the single source of publishable material content.
 * - Legacy BookAdminPanel is intentionally not exposed in the VOLUME workspace.
 * - The Almond product gets its approved four-space book architecture.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import AlmondBookContents from "@/pages/book/AlmondBookContents";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ChevronLeft, Check, Eye, BookOpen, PenLine, Search,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlmondBookMappingControlV1 } from "@/components/editorial-v2/AlmondBookMappingControlV1";
import { ALMOND_PRODUCT_ID } from "@/lib/almondPublicationCatalogV1.mjs";

const PRODUCT_STATUS_OPTIONS = [
  { value: "draft",     label: "Черновик" },
  { value: "review",    label: "На проверке" },
  { value: "published", label: "Опубликовано" },
  { value: "hidden",    label: "Скрыто" },
];

function materialDisplayTitle(material) {
  return material?._display_title || material?.title || "Материал";
}

export default function ProductWorkspace() {
  const { productId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawInitialTab = searchParams.get("tab") || "overview";
  const initialTab = rawInitialTab === "library" ? "content" : rawInitialTab;
  const [tab, setTab] = useState(initialTab);
  const [product, setProduct] = useState(null);
  const [editorialVolume, setEditorialVolume] = useState(null);
  const [editorialMaterials, setEditorialMaterials] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tomeSearch, setTomeSearch] = useState("");

  const isAdmin = user?.role === "owner" || user?.role === "administrator";
  const scopes = user?.product_scope_ids || [];
  const inScope = isAdmin || scopes.length === 0 || scopes.includes(productId);
  const isAlmond = productId === ALMOND_PRODUCT_ID;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [{ data: products }, { data: volumes }, { data: materials }] = await Promise.all([
          api.get("/admin/products"),
          api.get("/admin/editorial/volumes"),
          api.get("/admin/editorial/materials"),
        ]);
        const p = (products || []).find((x) => x.id === productId);
        if (!p) throw new Error("not-found");
        setProduct(p);

        const volume = (volumes || []).find((v) => v.product_id === productId) || null;
        setEditorialVolume(volume);

        let linked = volume ? (materials || []).filter((m) => m.volume_id === volume.id) : [];
        if (linked.length) {
          linked = await Promise.all(linked.map(async (m) => {
            try {
              const { data: blocks } = await api.get(`/admin/editorial/materials/${m.id}/blocks`);
              const hero = (blocks || []).find((b) => b.master_type === "HERO");
              const headline = hero?.content?.headline?.trim();
              return { ...m, _display_title: headline || m.title };
            } catch {
              return m;
            }
          }));
        }
        setEditorialMaterials(linked);
      } catch (e) {
        setError(e.message === "not-found" ? "Продукт не найден" : formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [productId]);

  const changeTab = (v) => {
    setTab(v);
    const next = new URLSearchParams(searchParams);
    if (v === "overview") next.delete("tab"); else next.set("tab", v);
    setSearchParams(next, { replace: true });
  };

  const saveSettings = async () => {
    if (!product) return;
    setSaving(true);
    try {
      const patch = {
        title: product.title,
        subtitle: product.subtitle,
        short_description: product.short_description,
        status: product.status,
        is_public: !!product.is_public,
        sort_order: product.sort_order ?? 0,
      };
      const { data } = await api.patch(`/admin/products/${productId}`, patch);
      setProduct(data);
      toast.success("Сохранено");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const openEditorial = (materialId) => {
    if (materialId) nav(`/admin/editorial-v2/materials/${materialId}`);
    else nav("/admin/editorial");
  };

  const openAsReader = () => {
    if (!product) return;
    if (isAlmond) {
      // Same tab, preserving the route back to Owner Content.
      nav(`/book/${encodeURIComponent(product.slug)}?asReader=1&from=tome-owner`);
      return;
    }
    if (product.type === "VOLUME") {
      window.open(`/book/${encodeURIComponent(product.slug)}/entry`, "_blank");
    } else {
      window.open(`/products/${encodeURIComponent(product.slug)}`, "_blank");
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-cream flex items-center justify-center"><div className="opacity-60 text-sm">Загрузка продукта…</div></div>;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="font-serif text-2xl mb-3 text-espresso">{error}</div>
          <Button onClick={() => nav("/admin?tab=products")}>К списку продуктов</Button>
        </div>
      </div>
    );
  }

  if (!inScope) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="font-serif text-2xl mb-3 text-espresso">Нет доступа к этому продукту</div>
          <Button variant="outline" onClick={() => nav("/admin?tab=products")}>Назад</Button>
        </div>
      </div>
    );
  }

  const isVolume = product?.type === "VOLUME";

  return (
    <div className="min-h-screen bg-[#F4EBDD]" data-testid="product-workspace" data-product-id={productId}>
      <header className="sticky top-0 z-30 border-b border-[#DCCDB8]/80 bg-[#F8F1E6]/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-5 py-3">
          <button onClick={() => nav("/admin?tab=products")} className="p-2 -ml-2 rounded-full hover:bg-white/60" aria-label="К списку продуктов">
            <ChevronLeft className="w-5 h-5 text-espresso" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] uppercase tracking-[0.32em] text-[#8E745A]">Сейчас вы работаете в</div>
            <h1 className="font-serif text-xl md:text-2xl text-espresso truncate -mt-0.5">{product.title}</h1>
          </div>
          <Button onClick={() => openEditorial()} variant="outline" className="hidden md:inline-flex rounded-full bg-white/50">
            <PenLine className="mr-2 h-4 w-4" /> Редакция
          </Button>
          <Button onClick={openAsReader} variant="outline" className="hidden md:inline-flex rounded-full bg-white/50">
            <Eye className="mr-2 h-4 w-4" /> Как читатель
          </Button>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-5 py-7">
        <Tabs value={tab} onValueChange={changeTab}>
          <div className="flex flex-wrap items-center justify-between gap-3" data-testid="tome-owner-tabs-search-row">
            <TabsList className="h-11 rounded-full bg-[#E9DDCB]/70 p-1">
              <TabsTrigger value="overview" className="rounded-full px-5">Обзор</TabsTrigger>
              {isVolume && <TabsTrigger value="content" className="rounded-full px-5">Содержание</TabsTrigger>}
              <TabsTrigger value="settings" className="rounded-full px-5">Настройки</TabsTrigger>
            </TabsList>
            {isAlmond && tab === "content" && (
              <label className="flex h-10 w-full min-w-0 items-center gap-2 rounded-full border border-[#D7C5AD] bg-white/65 px-4 sm:max-w-[410px]"
                data-testid="tome-owner-search-bar">
                <Search className="h-4 w-4 shrink-0 text-[#8A6947]" aria-hidden="true" />
                <input type="search" value={tomeSearch} onChange={(event) => setTomeSearch(event.target.value)}
                  placeholder="Поиск по названию или рецептуре" aria-label="Поиск по Тому"
                  className="min-w-0 flex-1 bg-transparent text-sm text-[#3A281E] outline-none placeholder:text-[#887565]"
                  data-testid="tome-owner-search" />
              </label>
            )}
          </div>

          <TabsContent value="overview" className="mt-7">
            <PremiumOverview
              product={product}
              isAlmond={isAlmond}
              editorialVolume={editorialVolume}
              materials={editorialMaterials}
              onGoContent={() => changeTab("content")}
              onEditorial={() => openEditorial()}
              onReader={openAsReader}
            />
          </TabsContent>

          {isVolume && (
            <TabsContent value="content" className={isAlmond ? "mt-4" : "mt-7"}>
              {isAlmond ? (
                <AlmondBookContents slugOverride={product.slug} embedded searchQuery={tomeSearch} onSearchQueryChange={setTomeSearch} />
              ) : (
                <GenericVolumeContent materials={editorialMaterials} onOpenEditorial={openEditorial} />
              )}
            </TabsContent>
          )}

          <TabsContent value="settings" className="mt-7">
            <SettingsPanel product={product} setProduct={setProduct} saving={saving} onSave={saveSettings} />
            {isAlmond && user?.role === "owner" && (
              <AlmondBookMappingControlV1 product={product} />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function PremiumOverview({ product, isAlmond, editorialVolume, materials, onGoContent, onEditorial, onReader }) {
  const statusLabel = PRODUCT_STATUS_OPTIONS.find((s) => s.value === product?.status)?.label || product?.status;
  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-[28px] border border-[#D7C5AD] bg-[#EADBC5]">
        <div className="grid min-h-[360px] md:grid-cols-[1.25fr_0.75fr]">
          <div className="flex flex-col justify-between p-7 md:p-10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.34em] text-[#8A6947]">{product?.type === "VOLUME" ? "Лаборатория превращений" : "Anokina Studio"}</div>
              <h2 className="mt-5 max-w-2xl font-serif text-5xl leading-[0.95] text-[#332015] md:text-6xl">{product?.title}</h2>
              <p className="mt-5 max-w-xl font-serif text-xl italic leading-relaxed text-[#644B36]">
                {isAlmond ? "Один ингредиент. Десятки превращений." : (product?.short_description || "")}
              </p>
              {isAlmond && <p className="mt-4 max-w-xl text-sm leading-6 text-[#68523F]">Премиальная интерактивная книга о вкусе, технологии, связях компонентов и авторской системе десертов.</p>}
            </div>
            <div className="mt-8 flex flex-wrap gap-2">
              <Button onClick={onGoContent} className="rounded-full bg-[#4A3425] px-6 text-[#FFF7EA] hover:bg-[#3A281D]">
                <BookOpen className="mr-2 h-4 w-4" /> Управлять содержанием
              </Button>
              <Button onClick={onEditorial} variant="outline" className="rounded-full border-[#9B7B59]/40 bg-white/35 px-6">
                <PenLine className="mr-2 h-4 w-4" /> Редакция
              </Button>
              <Button onClick={onReader} variant="outline" className="rounded-full border-[#9B7B59]/40 bg-white/35 px-6">
                <Eye className="mr-2 h-4 w-4" /> Как читатель
              </Button>
            </div>
          </div>
          <div className="relative min-h-[270px] border-t border-[#D7C5AD] bg-[#CDB391]/25 p-7 md:border-l md:border-t-0">
            <div className="absolute inset-0 opacity-25" style={{backgroundImage:"radial-gradient(circle at 65% 30%, rgba(255,255,255,.75), transparent 40%), linear-gradient(145deg, transparent 20%, rgba(86,54,31,.14))"}} />
            <div className="relative flex h-full flex-col justify-end">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#8A6947]">Состояние продукта</div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <Metric label="Статус" value={statusLabel} />
                <Metric label="Материалов" value={String(materials.length)} />
                <Metric label="Редакция" value={editorialVolume ? "Связана" : "Не связана"} />
                <Metric label="Доступ" value={isAlmond ? "Платный · бессрочный" : (product?.is_public ? "Публичная витрина" : "По доступу")} />
              </div>
            </div>
          </div>
        </div>
      </section>


    </div>
  );
}

function Metric({ label, value }) {
  return <div className="rounded-2xl border border-[#CDB897]/60 bg-white/35 p-4">
    <div className="text-[9px] uppercase tracking-[0.2em] text-[#8A6947]">{label}</div>
    <div className="mt-1 font-serif text-lg text-[#3A281E]">{value}</div>
  </div>;
}

function GenericVolumeContent({ materials, onOpenEditorial }) {
  return <div className="rounded-2xl border border-[#D7C5AD] bg-white/50 p-6">
    <div className="font-serif text-3xl text-[#332015]">Содержание</div>
    <div className="mt-5 space-y-2">
      {materials.map((m) => <button key={m.id} onClick={() => onOpenEditorial(m.id)} className="w-full rounded-xl border border-[#D7C5AD] bg-white/50 p-4 text-left font-serif text-lg">{materialDisplayTitle(m)}</button>)}
      {!materials.length && <div className="text-sm text-[#765F49]">Материалов пока нет.</div>}
    </div>
  </div>;
}

function SettingsPanel({ product, setProduct, saving, onSave }) {
  return (
    <div className="max-w-2xl rounded-[24px] border border-[#D7C5AD] bg-white/55 p-6 space-y-4">
      <div>
        <label className="block text-xs uppercase tracking-widest opacity-70 mb-1">Название</label>
        <Input value={product.title || ""} onChange={(e) => setProduct({ ...product, title: e.target.value })} />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest opacity-70 mb-1">Подзаголовок</label>
        <Input value={product.subtitle || ""} onChange={(e) => setProduct({ ...product, subtitle: e.target.value })} />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest opacity-70 mb-1">Короткое описание</label>
        <textarea value={product.short_description || ""} rows={3} onChange={(e) => setProduct({ ...product, short_description: e.target.value })} className="w-full rounded-xl border border-[#D7C5AD] bg-white/60 px-3 py-2 text-sm" />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest opacity-70 mb-1">Статус</label>
        <Select value={product.status || "draft"} onValueChange={(v) => setProduct({ ...product, status: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{PRODUCT_STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {product.id === ALMOND_PRODUCT_ID ? (
        <div className="border-t border-[#D7C5AD] pt-4">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!product.is_public} onChange={(e) => setProduct({ ...product, is_public: e.target.checked })} />
            <span>Показывать в «Программах Anokina Studio»</span>
          </label>
          <div className="mt-2 text-[11px] leading-5 text-[#7A6552]">
            Карточка и лендинг Тома будут видны до покупки. Содержание Тома открывается только пользователям с оплаченным доступом.
          </div>
        </div>
      ) : (
        <details className="border-t border-[#D7C5AD] pt-3">
          <summary className="cursor-pointer text-xs uppercase tracking-widest opacity-70">Дополнительные настройки</summary>
          <div className="mt-3 space-y-2">
            <label className="block text-xs opacity-70 mb-1">Slug (URL)</label>
            <Input value={product.slug || ""} onChange={(e) => setProduct({ ...product, slug: e.target.value })} />
            <label className="inline-flex items-center gap-2 mt-2 text-sm">
              <input type="checkbox" checked={!!product.is_public} onChange={(e) => setProduct({ ...product, is_public: e.target.checked })} />
              <span>Показывать на публичной витрине</span>
            </label>
          </div>
        </details>
      )}
      <div className="pt-4">
        <Button onClick={onSave} disabled={saving}><Check className="mr-2 h-4 w-4" />{saving ? "Сохранение…" : "Сохранить изменения"}</Button>
      </div>
    </div>
  );
}
